import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import { CSVUploadModal } from './Master';

// Status colours mirror the tracker sheet IT uses today
const STATUS = {
  'Completed':    { bg:'#93C47D', fg:'#12300a' },
  'Pending':      { bg:'#4285F4', fg:'#ffffff' },
  'User Pending': { bg:'#FF9900', fg:'#3a2200' },
  'In-Progress':  { bg:'#FFD966', fg:'#3d2f00' },
  'Dropped':      { bg:'#B7B7B7', fg:'#232323' },
};
const STATUSES = Object.keys(STATUS);
const PRIO = { P1:{ bg:'#F4CCCC', fg:'#7f1d1d' }, P2:{ bg:'#FFF2CC', fg:'#7c5800' }, P3:{ bg:'#D9EAD3', fg:'#274e13' } };

const Pill = ({ text, map }) => {
  const c = map[text];
  if (!text) return <span style={{ color:'var(--text3)' }}>—</span>;
  return (
    <span style={{ background:c?.bg || 'var(--bg3)', color:c?.fg || 'var(--text2)',
      fontSize:11, fontWeight:600, padding:'3px 10px', borderRadius:12, whiteSpace:'nowrap', display:'inline-block' }}>
      {text}
    </span>
  );
};
const fmt = d => d ? new Date(d).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'2-digit' }) : '—';
const blank = { campus_id:'', sno:'', task:'', started_on:'', priority:'', status:'Pending', assigned_to_name:'', target_date:'', remarks:'' };

export default function MeetingPoints({ go }) {
  const { user, isAdmin } = useAuth();
  const seesAll = ['admin','it_head'].includes(user?.role);
  const [rows, setRows] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [itUsers, setItUsers] = useState([]);   // IT people this point can be assigned to
  const [f, setF] = useState({ campus_id:'', status:'', priority:'', search:'' });
  const [modal, setModal] = useState(null);   // null | {…row}
  const [csv, setCsv] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    const params = {}; Object.entries(f).forEach(([k,v]) => { if (v) params[k]=v; });
    api.get('/meeting-points', { params }).then(r => setRows(r.data)).catch(()=>{}).finally(()=>setLoading(false));
  }, [f]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/master/campuses').then(r => setCampuses(r.data.filter(c=>c.active))).catch(()=>{});
    api.get('/tasks/it-users').then(r => setItUsers(r.data)).catch(()=>{});
  }, []);

  // "Assigned To" is stored as a name string ("Sivaraj & Aadhithya") to match the
  // sheet. Selecting IT people builds that string; names that aren't portal users
  // (imported values like "Maintenance") are preserved.
  const assignedList = (modal?.assigned_to_name || '').split('&').map(s => s.trim()).filter(Boolean);
  const assignedExtras = assignedList.filter(n => !itUsers.some(u => u.full_name === n));
  const toggleAssignee = name => {
    const next = assignedList.includes(name) ? assignedList.filter(n => n !== name) : [...assignedList, name];
    setModal(m => ({ ...m, assigned_to_name: next.join(' & ') }));
  };

  const openNew = () => {
    const myCampus = seesAll ? '' : String(user.campus_ids?.[0] || '');
    setModal({ ...blank, campus_id: myCampus }); setErr('');
  };
  const openEdit = r => setModal({
    ...r, campus_id:String(r.campus_id), sno:r.sno ?? '',
    started_on: r.started_on ? r.started_on.slice(0,10) : '',
    target_date: r.target_date ? r.target_date.slice(0,10) : '',
    priority:r.priority || '', assigned_to_name:r.assigned_to_name || '', remarks:r.remarks || '',
  });
  const save = async () => {
    if (!modal.campus_id)     { setErr('Select a campus'); return; }
    if (!modal.task.trim())   { setErr('Task is required'); return; }
    setBusy(true); setErr('');
    const body = { ...modal, campus_id: parseInt(modal.campus_id), sno: modal.sno ? parseInt(modal.sno) : null };
    try {
      if (modal.id) await api.put(`/meeting-points/${modal.id}`, body);
      else await api.post('/meeting-points', body);
      setModal(null); load();
    } catch(e) { setErr(e.response?.data?.error || 'Save failed'); } finally { setBusy(false); }
  };
  const del = async r => {
    if (!window.confirm(`Delete this point?\n\n"${r.task}"`)) return;
    try { await api.delete(`/meeting-points/${r.id}`); load(); }
    catch(e) { window.alert(e.response?.data?.error || 'Delete failed'); }
  };

  const counts = STATUSES.map(s => [s, rows.filter(r => r.status === s).length]);

  return (
    <div>
      <div className="flex-gap" style={{ justifyContent:'space-between', alignItems:'flex-start', marginBottom:14, flexWrap:'wrap', gap:10 }}>
        <div>
          <h2 style={{ fontSize:18, fontWeight:600, marginBottom:4 }}>📌 Meeting Points — IT Pending Tracker</h2>
          <p style={{ fontSize:13, color:'var(--text2)' }}>
            {seesAll ? 'All campuses.' : 'Your campus.'} Points reviewed in the IT meeting —
            including tickets flagged <b>“discuss in meeting”</b>.
          </p>
        </div>
        <div className="flex-gap" style={{ gap:8 }}>
          <button className="btn btn-secondary" onClick={() => setCsv(true)}>⬆ Bulk Upload</button>
          <button className="btn btn-primary" onClick={openNew}>+ Add Point</button>
        </div>
      </div>

      {/* Status legend / counts */}
      <div className="flex-gap" style={{ gap:8, marginBottom:12, flexWrap:'wrap' }}>
        {counts.map(([s, n]) => (
          <span key={s} onClick={() => setF(x => ({ ...x, status: x.status===s ? '' : s }))}
            style={{ cursor:'pointer', background:STATUS[s].bg, color:STATUS[s].fg, fontSize:12, fontWeight:600,
              padding:'4px 12px', borderRadius:14, opacity: f.status && f.status!==s ? 0.45 : 1 }}>
            {s}: {n}
          </span>
        ))}
      </div>

      <div className="flex-gap" style={{ gap:8, marginBottom:12, flexWrap:'wrap' }}>
        <input placeholder="Search task / remarks / person…" value={f.search}
          onChange={e => setF(x => ({ ...x, search:e.target.value }))} style={{ padding:'6px 8px', minWidth:220 }} />
        {seesAll && (
          <select value={f.campus_id} onChange={e => setF(x => ({ ...x, campus_id:e.target.value }))} style={{ padding:'6px 8px' }}>
            <option value="">All campuses</option>
            {campuses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        )}
        <select value={f.status} onChange={e => setF(x => ({ ...x, status:e.target.value }))} style={{ padding:'6px 8px' }}>
          <option value="">All statuses</option>{STATUSES.map(s => <option key={s}>{s}</option>)}
        </select>
        <select value={f.priority} onChange={e => setF(x => ({ ...x, priority:e.target.value }))} style={{ padding:'6px 8px' }}>
          <option value="">All priorities</option><option>P1</option><option>P2</option><option>P3</option>
        </select>
      </div>

      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        {loading ? <div style={{ padding:40, textAlign:'center', color:'var(--text2)' }}>Loading…</div>
        : !rows.length ? <div className="empty-state"><p>No meeting points yet. Add one or bulk upload your tracker sheet.</p></div>
        : (
          <div className="table-wrap"><table>
            <thead><tr>
              <th style={{ width:50 }}>S.No</th><th>Task</th>{seesAll && <th>Campus</th>}
              <th>Started</th><th>Priority</th><th>Status</th><th>Assigned To</th><th>Target</th><th>Remarks</th><th></th>
            </tr></thead>
            <tbody>{rows.map(r => {
              const overdue = r.target_date && r.status !== 'Completed' && new Date(r.target_date) < new Date().setHours(0,0,0,0);
              return (
                <tr key={r.id}>
                  <td style={{ fontSize:12, color:'var(--text2)' }}>{r.sno ?? '—'}</td>
                  <td style={{ fontWeight:500, fontSize:13, minWidth:200 }}>
                    {r.task}
                    {r.source === 'ticket' && (
                      <div style={{ fontSize:11, color:'var(--accent)', marginTop:2 }}>
                        🎫 From ticket <span className="mono">{r.ticket_ref}</span>
                      </div>
                    )}
                  </td>
                  {seesAll && <td style={{ fontSize:12, color:'var(--text2)' }}>{r.campus_name}</td>}
                  <td style={{ fontSize:12, whiteSpace:'nowrap' }}>{fmt(r.started_on)}</td>
                  <td><Pill text={r.priority} map={PRIO} /></td>
                  <td><Pill text={r.status} map={STATUS} /></td>
                  <td style={{ fontSize:12 }}>{r.assigned_to_name || '—'}</td>
                  <td style={{ fontSize:12, whiteSpace:'nowrap', color: overdue ? 'var(--red)' : 'var(--text)', fontWeight: overdue ? 600 : 400 }}>
                    {fmt(r.target_date)}{overdue ? ' ⚠' : ''}
                  </td>
                  <td style={{ fontSize:12, color:'var(--text2)', maxWidth:260 }}>{r.remarks || '—'}</td>
                  <td><div className="flex-gap" style={{ gap:6 }}>
                    {r.source === 'ticket' ? (
                      // Owned by the ticket — edit it there, not here
                      <button className="btn btn-secondary btn-sm" onClick={() => go && go('tasks')}>Open ticket</button>
                    ) : (<>
                      <button className="btn btn-secondary btn-sm" onClick={() => openEdit(r)}>Edit</button>
                      {isAdmin && <button className="btn btn-danger btn-sm" onClick={() => del(r)}>Delete</button>}
                    </>)}
                  </div></td>
                </tr>
              );
            })}</tbody>
          </table></div>
        )}
      </div>

      {modal && (
        <div className="modal-overlay" onClick={e => e.target===e.currentTarget && false && setModal(null)}>
          <div className="modal" style={{ maxWidth:620, maxHeight:'92vh', overflowY:'auto' }}>
            <div className="modal-header"><span className="modal-title">{modal.id ? 'Edit Point' : 'Add Meeting Point'}</span>
              <button className="modal-close" onClick={() => setModal(null)}>×</button></div>
            <div className="form-row">
              <div className="form-group"><label>Campus *</label>
                <select value={modal.campus_id} disabled={!!modal.id} onChange={e => setModal(m => ({ ...m, campus_id:e.target.value }))}>
                  <option value="">— Select —</option>
                  {campuses.filter(c => seesAll || (user.campus_ids||[]).includes(c.id)).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select></div>
              <div className="form-group"><label>S.No</label>
                <input type="number" value={modal.sno} onChange={e => setModal(m => ({ ...m, sno:e.target.value }))} /></div>
            </div>
            <div className="form-group"><label>Task *</label>
              <input value={modal.task} onChange={e => setModal(m => ({ ...m, task:e.target.value }))} placeholder="e.g. CCTV Room Camera relocation" /></div>
            <div className="form-row">
              <div className="form-group"><label>Started On</label>
                <input type="date" value={modal.started_on} onChange={e => setModal(m => ({ ...m, started_on:e.target.value }))} /></div>
              <div className="form-group"><label>Target Date</label>
                <input type="date" value={modal.target_date} onChange={e => setModal(m => ({ ...m, target_date:e.target.value }))} /></div>
            </div>
            <div className="form-row">
              <div className="form-group"><label>Priority</label>
                <select value={modal.priority} onChange={e => setModal(m => ({ ...m, priority:e.target.value }))}>
                  <option value="">—</option><option>P1</option><option>P2</option><option>P3</option>
                </select></div>
              <div className="form-group"><label>Status</label>
                <select value={modal.status} onChange={e => setModal(m => ({ ...m, status:e.target.value }))}>
                  {STATUSES.map(s => <option key={s}>{s}</option>)}
                </select></div>
            </div>
            <div className="form-group">
              <label>Assigned To <span style={{ fontSize:11, color:'var(--text2)' }}>— pick one or more IT people</span></label>
              <div style={{ display:'flex', flexWrap:'wrap', gap:'6px 14px', padding:'8px 10px',
                background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:'var(--radius)' }}>
                {itUsers.length === 0 && <span style={{ fontSize:12, color:'var(--text2)' }}>No IT users found.</span>}
                {itUsers.map(u => {
                  const on = assignedList.includes(u.full_name);
                  return (
                    <label className="opt" key={u.id} style={{ display:'inline-flex', alignItems:'center', gap:6, fontSize:13 }}>
                      <input type="checkbox" checked={on} onChange={() => toggleAssignee(u.full_name)} />
                      {u.full_name}
                    </label>
                  );
                })}
                {/* Names from the imported sheet that aren't portal users (e.g. "Maintenance") are kept */}
                {assignedExtras.map(n => (
                  <label className="opt" key={n} style={{ display:'inline-flex', alignItems:'center', gap:6, fontSize:13, color:'var(--text2)' }}>
                    <input type="checkbox" checked onChange={() => toggleAssignee(n)} />
                    {n} <span style={{ fontSize:10 }}>(from sheet)</span>
                  </label>
                ))}
              </div>
              <span style={{ fontSize:11, color:'var(--text2)' }}>
                Selected: <b>{modal.assigned_to_name || '—'}</b>
              </span>
            </div>
            <div className="form-group"><label>Remarks</label>
              <textarea rows={3} value={modal.remarks} onChange={e => setModal(m => ({ ...m, remarks:e.target.value }))} /></div>
            {err && <div className="err-msg">{err}</div>}
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={busy}>{busy?'Saving…':'Save'}</button>
            </div>
          </div>
        </div>
      )}

      {csv && (
        <CSVUploadModal
          title="Bulk Upload Meeting Points"
          sampleFile={{ name:'meeting-points-sample.csv',
            content: 'Campus,S.No,Task,Started On,Priority,Status,Assigned To,Target Date,Remarks\n'
              + 'CBE-MAIN,21,CCTV Room Camera relocation,2026-06-11,P1,Completed,Maintenance,2026-06-12,Will complete tomorrow\n'
              + 'CBE-MAIN,8,Jio SIP move to new account,2025-10-11,P2,User Pending,Sivaraj,2026-06-25,Jio will change the account\n'
              + 'CBE-MAIN,16,DP & MS/HS Block 5 Rack cleaning,,P1,Pending,Sivaraj & Aadhithya,2026-06-26,Will complete one by one\n' }}
          formatHint={
            <>
              <strong>Required:</strong> Campus, Task<br />
              <strong>Optional:</strong> S.No, Started On, Priority (P1/P2/P3), Status, Assigned To, Target Date, Remarks<br />
              <strong>Status:</strong> Pending · In-Progress · User Pending · Completed · Dropped (blank = Pending)<br />
              <strong>Dates:</strong> <span className="mono">2026-06-11</span> or <span className="mono">11-06-2026</span>.
              <strong> Campus</strong> = code or name — this is what splits the tracker per campus.
            </>
          }
          columns={[{key:'campus',label:'Campus'},{key:'sno',label:'S.No'},{key:'task',label:'Task'},
            {key:'priority',label:'Priority'},{key:'status',label:'Status'},{key:'assigned_to',label:'Assigned To'},{key:'target_date',label:'Target'}]}
          mapRow={r => {
            // parseCSV lower-cases headers and turns spaces into underscores
            const pick = (...k) => { for (const x of k) { const v = r[x]; if (v && String(v).trim()) return String(v).trim(); } return ''; };
            const task = pick('task','point','work','description');
            const campus = pick('campus','campus_code','site');
            if (!task || !campus) return null;
            return { campus, task,
              sno:         pick('sno','s_no','sl_no','serial','no'),
              started_on:  pick('started_on','start_date','started','from_date'),
              priority:    pick('priority','p'),
              status:      pick('status'),
              assigned_to: pick('assigned_to','assignedto','owner','person','responsible'),
              target_date: pick('target_date','target','due_date','due','eta'),
              remarks:     pick('remarks','remark','notes','comments','update'),
            };
          }}
          onUpload={rws => api.post('/meeting-points/import', { records: rws }).then(r => r.data)}
          onDone={load}
          onClose={() => setCsv(false)}
        />
      )}
    </div>
  );
}
